<template>
  <div class="tw-my-4 md:tw-my-8">
    <!-- Enhanced Quote Summary Header -->
    <v-card class="mx-auto tw-py-2 md:tw-py-3 tw-mb-6" outlined elevation="8">
      <v-card-text class="tw-p-6">
        <div class="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <div>
            <h2 class="c-blue-text tw-font-bold tw-text-2xl tw-mb-2">Your Travel Insurance Quote</h2>
            <p class="tw-text-gray-600">Quote ID: #{{ quote.id || 'TRV-' + Date.now() }}</p>
          </div>
          <div class="tw-text-right">
            <v-chip :color="quote.sold ? 'success' : 'orange'" size="large">
              {{ quote.sold ? 'Purchased' : 'Active Quote' }}
            </v-chip>
            <div class="tw-mt-2 tw-text-sm tw-text-gray-500">
              Created: {{ formatDate(quote.created_at || Date.now()) }}
            </div>
          </div>
        </div>

        <!-- Quote Summary Cards -->
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mt-6">
          <v-card class="tw-bg-blue-50 tw-border tw-border-blue-200" flat>
            <v-card-text class="tw-p-4 tw-text-center">
              <v-icon size="32" color="primary" class="tw-mb-2">mdi-shield-check</v-icon>
              <div class="tw-text-sm tw-text-gray-600">Coverage Amount</div>
              <div class="tw-text-xl tw-font-bold c-blue-text">${{ quote.coverage_amount | formatNum }}</div>
            </v-card-text>
          </v-card>

          <v-card class="tw-bg-green-50 tw-border tw-border-green-200" flat>
            <v-card-text class="tw-p-4 tw-text-center">
              <v-icon size="32" color="success" class="tw-mb-2">mdi-currency-usd</v-icon>
              <div class="tw-text-sm tw-text-gray-600">Premium</div>
              <div class="tw-text-xl tw-font-bold tw-text-green-600">${{ quote.premium | formatNum }}</div>
              <div class="tw-text-sm tw-text-gray-500">Kshs. {{ premiumInKsh | formatNum }}</div>
            </v-card-text>
          </v-card>

          <v-card class="tw-bg-purple-50 tw-border tw-border-purple-200" flat>
            <v-card-text class="tw-p-4 tw-text-center">
              <v-icon size="32" color="purple" class="tw-mb-2">mdi-calendar-range</v-icon>
              <div class="tw-text-sm tw-text-gray-600">Duration</div>
              <div class="tw-text-lg tw-font-bold tw-text-purple-600">{{ getDurationText() }}</div>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mx-auto tw-py-2 md:tw-py-3" outlined>
      <v-card class="mx-auto tw-px-2 md:tw-px-4 tw-py-2 md:tw-py-3" flat>
        <h2 class="c-blue-text tw-font-bold tw-px-0 md:tw-px-8 tw-mb-4">Travel Details</h2>
        <div class="tw-flex tw-flex-wrap tw-space-x-3 md:tw-space-x-10 tw-items-start tw-justify-between tw-px-2 md:tw-px-8">
          <div class="tw-flex tw-flex-wrap tw-px-2 md:tw-px-8">
            <div class="tw-flex md:tw-block tw-justify-between tw-w-full md:tw-w-auto tw-mb-2">
              <p class="c-gray-text"><strong class="c-navy-text tw-uppercase">Destination: </strong> {{ quote.destination }} </p>
            </div>
            <div class="tw-flex md:tw-block tw-justify-between tw-w-full md:tw-w-auto tw-mb-2">
              <p class="c-gray-text"><strong class="c-navy-text tw-uppercase">Travel Type: </strong> {{ quote.travel_type | capitalize }} </p>
            </div>
            <div class="tw-flex md:tw-block tw-justify-between tw-w-full md:tw-w-auto tw-mb-2">
              <p class="c-gray-text"><strong class="c-navy-text tw-uppercase">Travel Dates: </strong> {{ quote.start_date }} to {{ quote.end_date }} </p>
            </div>
            <div class="tw-flex md:tw-block tw-justify-between tw-w-full md:tw-w-auto tw-mb-2">
              <p class="c-gray-text"><strong class="c-navy-text tw-uppercase">Number of Travelers: </strong> {{ quote.number_of_travelers }} </p>
            </div>
          </div>
        </div>
      </v-card>
      <div class="tw-px-2 md:tw-px-8 tw-py-3 md:tw-py-6">
        <hr />
      </div>
      <v-card class="mx-auto tw-px-2 md:tw-px-4 tw-py-2 md:tw-py-3" flat>
        <h2 class="c-blue-text tw-font-bold tw-px-0 md:tw-px-8 tw-mb-4">Coverage Details</h2>
        <div class="tw-flex tw-flex-wrap tw-space-x-3 md:tw-space-x-10 tw-items-start tw-px-2 md:tw-px-8">
          <div class="tw-flex md:tw-block tw-px-2 tw-justify-between tw-w-full md:tw-w-auto tw-mb-3 md:tw-mb-0">
            <h2 class="c-navy-text tw-font-bold">MEDICAL EXPENSES</h2>
          </div>
          <div class="tw-px-0 md:tw-px-8">
            <p class="tw-text-sm c-gray-text">Coverage for medical emergencies and treatment during travel.</p>
            <h2 class="c-navy-text tw-font-bold tw-text-xl" >LIMIT: USD {{ quote.coverage_amount | formatNum }} </h2>
          </div>
        </div>
      </v-card>
      <v-card class="mx-auto tw-px-2 md:tw-px-4 tw-py-2 md:tw-py-3" flat>
        <h2 class="c-blue-text tw-font-bold tw-px-0 md:tw-px-8 tw-mb-4">Additional Benefits</h2>
        <div class="tw-flex tw-flex-wrap tw-space-x-3 md:tw-space-x-10 tw-items-start tw-px-2 md:tw-px-8">
          <div class="tw-flex md:tw-block tw-px-2 tw-justify-between tw-w-full md:tw-w-auto tw-mb-3 md:tw-mb-0">
            <h2 class="c-navy-text tw-font-bold">TRIP CANCELLATION</h2>
          </div>
          <div class="tw-px-0 md:tw-px-8">
            <p class="tw-text-sm c-gray-text">Coverage for non-refundable trip costs due to covered reasons.</p>
            <h2 class="c-navy-text tw-font-bold tw-text-xl" >LIMIT: USD {{ (quote.coverage_amount * 0.8) | formatNum }} </h2>
          </div>
        </div>
      </v-card>
      <v-card class="mx-auto tw-px-2 md:tw-px-4 tw-py-2 md:tw-py-3" flat>
        <div class="tw-flex tw-flex-wrap tw-space-x-3 md:tw-space-x-10 tw-items-start tw-px-2 md:tw-px-8">
          <div class="tw-flex md:tw-block tw-px-2 tw-justify-between tw-w-full md:tw-w-auto tw-mb-3 md:tw-mb-0">
            <h2 class="c-navy-text tw-font-bold">LUGGAGE PROTECTION</h2>
          </div>
          <div class="tw-px-0 md:tw-px-8">
            <p class="tw-text-sm c-gray-text">Coverage for lost, stolen, or damaged luggage and personal items.</p>
            <h2 class="c-navy-text tw-font-bold tw-text-xl" >LIMIT: USD {{ (quote.coverage_amount * 0.1) | formatNum }} </h2>
          </div>
        </div>
      </v-card>
      <v-card class="mx-auto tw-px-2 md:tw-px-4 tw-py-2 md:tw-py-3" flat>
        <div class="tw-flex tw-flex-wrap tw-space-x-3 md:tw-space-x-10 tw-items-start tw-px-2 md:tw-px-8">
          <div class="tw-flex md:tw-block tw-px-2 tw-justify-between tw-w-full md:tw-w-auto tw-mb-3 md:tw-mb-0">
            <h2 class="c-navy-text tw-font-bold">EMERGENCY EVACUATION</h2>
          </div>
          <div class="tw-px-0 md:tw-px-8">
            <p class="tw-text-sm c-gray-text">Coverage for emergency medical evacuation to nearest adequate facility.</p>
            <h2 class="c-navy-text tw-font-bold tw-text-xl" >LIMIT: USD {{ 1000000 | formatNum }} </h2>
          </div>
        </div>
      </v-card>
      <!-- Enhanced Action Section -->
      <div class="tw-px-3 md:tw-px-10 tw-py-6">
        <v-card class="tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-800 tw-text-white" flat>
          <v-card-text class="tw-p-6">
            <div class="tw-flex tw-flex-wrap tw-justify-between tw-items-center">
              <div>
                <h2 class="tw-text-white tw-font-bold tw-text-xl md:tw-text-2xl tw-mb-2">
                  Premium Payable: USD {{ quote.premium | formatNum }} (Kshs. {{ premiumInKsh | formatNum }})
                </h2>
                <p class="tw-text-blue-100 tw-text-sm">
                  This quote is valid for 30 days from the date of creation
                </p>
              </div>
              <div class="tw-flex tw-flex-wrap tw-gap-3 tw-mt-4 md:tw-mt-0">
                <v-btn
                  color="white"
                  variant="flat"
                  class="tw-text-blue-800 tw-font-semibold"
                  size="large"
                  @click="buyTravel"
                >
                  <v-icon start>mdi-cart</v-icon>
                  Buy Now
                </v-btn>
                <!-- <v-btn
                  color="success"
                  variant="flat"
                  class="tw-text-white tw-font-semibold"
                  size="large"
                  @click="buyInstantly"
                  :loading="instantBuyLoading"
                >
                  <v-icon start>mdi-lightning-bolt</v-icon>
                  Instant Policy
                </v-btn> -->
                <v-btn
                  color="white"
                  variant="outlined"
                  class="tw-text-white tw-border-white hover:tw-bg-white hover:tw-text-blue-800"
                  @click="startOver"
                >
                  <v-icon start>mdi-refresh</v-icon>
                  New Quote
                </v-btn>
                <EmailTravelQuotation />
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Additional Actions -->
        <div class="tw-mt-4 tw-flex tw-flex-wrap tw-gap-3 tw-justify-center">
          <v-btn
            variant="outlined"
            color="secondary"
            @click="shareQuote"
          >
            <v-icon start>mdi-share</v-icon>
            Share Quote
          </v-btn>
          <v-btn
            variant="outlined"
            color="info"
            @click="compareQuotes"
          >
            <v-icon start>mdi-compare</v-icon>
            Compare Plans
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>

import EmailTravelQuotation from '@/components/rfq/travel/EmailTravelQuotation';
import helpers from '@/utils/helpers';
import { mapGetters } from 'vuex';
import exchangeRates from '@/config/exchangeRates';
import { travelPaymentReference } from '@/utils/travelPaymentReference';

export default {
  name: 'TravelQuoteSummary',
  components: {
    EmailTravelQuotation,
  },
  props: {
    showActions: {
      type: Boolean,
      default: true,
    },
    standalone: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      instantBuyLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      quote: 'rfq/getTravelQuoteResponse',
    }),
    exchangeRate() {
      return exchangeRates.getUsdToKshRate();
    },
    premiumInKsh() {
      return Math.round(this.quote.premium * this.exchangeRate);
    },
  },
  filters: {
    capitalize: (value) => {
      if (!value) return '';
      const stringValue = value.toString();
      return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
    },
    formatNum: helpers.formatNum,
  },
  methods: {
    startOver() {
      // Clear any existing travel quote data from store
      this.$store.commit('rfq/setTravelQuoteResponse', {});
      this.$store.commit('rfq/clearTravelFormData');
      this.$store.commit('rfq/clearTravelQuotation');

      // Navigate back to the travel quote request page
      this.$router.push({
        name: 'Request',
        query: { b: 'travel' },
      }).then(() => {
        // console.log('Navigation to new travel quote completed');
      }).catch(() => {
        // console.error('Navigation failed:', error);
        // Fallback: emit the original event if navigation fails
        this.$root.$emit('start-travel-over');
      });
    },
    buyTravel() {
      // console.log('buyTravel clicked');
      // console.log('isAuthenticated:', this.$store.getters['auth/isAuthenticated']);
      this.createPolicyAndGoToPayment();
    },

    createPolicyAndGoToPayment() {
      // console.log('createPolicyAndGoToPayment called - going directly to payment');
      // Go directly to payment - policy will be issued after successful payment
      const formData = {
        first_name: 'Travel',
        last_name: 'Customer',
        email: this.quote.email_address || 'customer@example.com',
        phone_number: '+254700000000',
      };

      // Convert USD premium to KSH for payment
      const kshAmount = this.premiumInKsh;

      // console.log(`Converting USD $${this.quote.premium} to KSH ${kshAmount} (rate: ${this.exchangeRate})`);

      // Set up payment context without creating policy first
      const payment = {
        payableAmount: kshAmount, // Convert to KSH
        product: 'Travel Insurance',
        productType: 'travel',
        productId: this.quote.id, // Use quote ID since no policy yet
        email: this.quote.email_address || formData.email,
        phoneNumber: formData.phone_number,
        transactionReference: travelPaymentReference(this.quote?.id_no || this.quote?.id_number || this.quote?.passport_no || this.quote?.passport),
        transactionDescription: `TV_rfq-${this.quote.id}`,
        quoteData: this.quote,
        formData,
      };

      // console.log('Setting payment context:', payment);
      this.$store.commit('payments/setPaymentContext', payment);

      // Navigate to BuyCover with travel parameter to trigger MakePayment flow like DP
      this.$router.push({
        name: 'BuyCover',
        query: {
          q: this.quote.id,
          b: 'travel',
          payment: true, // Flag to indicate direct payment
        },
      });
    },

    async buyInstantly() {
      if (!this.quote || !this.quote.id) {
        this.$toast.error('Quote not found. Please generate a new quote.', 'Error');
        return;
      }

      // Check if user is authenticated
      if (!this.$store.getters['auth/isAuthenticated']) {
        this.$toast.info('Please log in to purchase a policy instantly', 'Login Required');
        this.$router.push({
          name: 'SignIn',
          query: {
            r: this.$route.fullPath,
            action: 'instant-buy',
            quoteId: this.quote.id,
          },
        });
        return;
      }

      this.instantBuyLoading = true;

      try {
        // Check if user has complete profile information required for policy
        const user = this.$store.getters['auth/authenticatedUser'];
        const requiredFields = ['given_name', 'family_name', 'email', 'phone_number'];
        const missingFields = requiredFields.filter((field) => !user[field]);

        if (missingFields.length > 0 || !user['custom:id_number'] || !user['custom:date_of_birth']) {
          this.instantBuyLoading = false;
          this.$toast.info('Please complete your profile to use instant policy purchase', 'Profile Incomplete');
          this.$router.push({ name: 'UpdateProfile', query: { r: this.$route.fullPath } });
          return;
        }

        // Auto-populate form data from user profile
        const formData = {
          first_name: user.given_name,
          last_name: user.family_name,
          email: user.email,
          phone_number: user.phone_number,
          id_number: user['custom:id_number'],
          date_of_birth: user['custom:date_of_birth'],
          gender: user['custom:gender'] || 'M',
          passport_number: user['custom:passport_number'] || `TEMP${Date.now()}`,
          agent_option: 1, // Direct client
          politically_exposed: false,
          travel_start_date: this.quote.start_date,
          travel_end_date: this.quote.end_date,
          declaration_acceptance: true,
          data_privacy_consent: true,
          // Emergency contact - use a default if not available
          emergency_contact_name: user['custom:emergency_contact_name'] || 'Emergency Contact',
          emergency_contact_phone: user['custom:emergency_contact_phone'] || user.phone_number,
          next_of_kin_relationship: user['custom:emergency_relationship'] || 'Relative',
          // Address - use a default for instant purchase
          address: user['custom:address'] || 'Nairobi, Kenya',
          coordinates: { latitude: -1.2921, longitude: 36.8219 }, // Default to Nairobi
        };

        // Create local policy record first
        const localPolicyResp = await this.$axios.post('customer/digital-quotation/pacis-travel/policy', {
          ...formData,
          quote_id: this.quote.id,
        });

        // Transform data for Mapfre API
        const mapfrePolicyData = this.transformToMapfrePayload(this.quote, formData);

        // Issue policy with Mapfre using existing GIS API (requires authentication)
        const mapfreResp = await this.$axios.post('https://prod.api.pacisinsurance.com/customer/gis/mapfre/policy/issue', mapfrePolicyData, {
          headers: {
            Authorization: 'Bearer {{authToken}}',
            'Content-Type': 'application/json',
          },
        });

        if (mapfreResp.data.success) {
          const policyData = mapfreResp.data.data;

          // Update local policy with Mapfre details
          await this.$axios.patch(`customer/digital-quotation/pacis-travel/policy/${localPolicyResp.data.id}`, {
            mapfre_policy_number: policyData.policyNumber || policyData.contractNumber,
            mapfre_contract_number: policyData.contractNumber,
            mapfre_guid: policyData.guid,
            mapfre_file_url: policyData.fileUrl,
            status: 'issued',
            purchase_method: 'instant',
          });

          // Store policy details in Vuex
          this.$store.commit('rfq/setTravelPolicyResult', {
            localPolicy: localPolicyResp.data,
            mapfrePolicy: policyData,
            quote: this.quote,
          });

          this.instantBuyLoading = false;

          // Show success message
          this.$toast.success('Travel insurance policy issued instantly!', 'Success');

          // Redirect to policy result page
          this.$router.push({
            name: 'PolicyResult',
            params: {
              policyId: localPolicyResp.data.id,
              type: 'travel',
            },
          });
        } else {
          throw new Error(mapfreResp.data.message || 'Policy issuance failed');
        }
      } catch (error) {
        this.instantBuyLoading = false;
        // console.error('Instant policy purchase error:', error);

        let errorMessage = 'Failed to issue policy instantly. ';

        if (error.response?.data?.message) {
          errorMessage += error.response.data.message;
        } else if (error.message) {
          errorMessage += error.message;
        } else {
          errorMessage += 'Please try the standard purchase flow.';
        }

        this.$toast.error(errorMessage, 'Instant Purchase Failed');

        // Fall back to standard purchase flow
        this.buyTravel();
      }
    },

    // Enhanced methods
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      try {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      } catch (error) {
        return 'N/A';
      }
    },

    getDurationText() {
      try {
        // Try different possible date field combinations
        const startDate = this.quote.start_date || this.quote.travel_start_date || this.quote.startDate;
        const endDate = this.quote.end_date || this.quote.travel_end_date || this.quote.endDate;

        // Check if dates exist and are not placeholder values
        if (startDate && endDate && startDate !== 'TBD' && endDate !== 'TBD') {
          const start = new Date(startDate);
          const end = new Date(endDate);

          // Check if dates are valid
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            // console.warn('Invalid dates provided:', { startDate, endDate });
            return this.quote.duration || 'N/A';
          }

          const diffTime = Math.abs(end - start);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          // Ensure diffDays is a valid number
          if (Number.isNaN(diffDays) || diffDays <= 0) {
            // console.warn('Invalid duration calculation:', {
            //   start, end, diffTime, diffDays,
            // });
            return this.quote.duration || 'N/A';
          }

          return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
        }

        // Fallback to duration field or default
        return this.quote.duration || 'N/A';
      } catch (error) {
        // console.error('Error calculating duration:', error);
        return this.quote.duration || 'N/A';
      }
    },

    downloadQuote() {
      // Implement quote download functionality
      this.$toast.info('Quote download feature coming soon!', 'Info');
    },

    shareQuote() {
      // Implement quote sharing functionality
      if (navigator.share) {
        navigator.share({
          title: 'My Travel Insurance Quote',
          text: `Check out my travel insurance quote for ${this.quote.destination}`,
          url: window.location.href,
        }).catch(() => {
          // console.log('Error sharing:', err);
          this.fallbackShare();
        });
      } else {
        this.fallbackShare();
      }
    },

    fallbackShare() {
      // Fallback share method
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        this.$toast.success('Quote link copied to clipboard!', 'Success');
      }).catch(() => {
        this.$toast.info(`Quote URL: ${url}`, 'Share');
      });
    },

    compareQuotes() {
      // Implement quote comparison functionality
      this.$toast.info('Quote comparison feature coming soon!', 'Info');
    },

    // Mapfre transformation methods (shared with PersonalDetailsTravel)
    transformToMapfrePayload(quoteData, formData) {
      // Map destination to zone and product code
      const { zone, prodCode } = this.getZoneAndProductCode(quoteData);

      // Build insured data array matching backend structure
      const insuredData = [{
        insuredFirstName: formData.first_name,
        insuredSurname: formData.last_name,
        // insuredFirstName: 'TEST',
        // insuredSurname: 'TEST',
        insuredDateOfBirth: formData.date_of_birth,
        insuredPassport: formData.passport_number || `TEMP${Date.now()}`,
      }];

      // Build payload matching IssuePolicyRequest structure
      const payload = {
        prodCode,
        policyNumber: 'AUTO',
        email: formData.email || quoteData.email_address,
        startDate: formData.travel_start_date || quoteData.start_date,
        endDate: formData.travel_end_date || quoteData.end_date,
        agnShtDesc: formData.agent_option === 1 ? 'DIRECT' : 'AGENT',
        comment: `Travel to ${quoteData.destination} - Instant Portal booking`,
        zone: zone.toString(),
        destinationCountry: this.getCountryCode(quoteData.destination),
        originCountry: 'KE',
        insuredData,
        policyholderFirstName: formData.first_name,
        policyholderSurname: formData.last_name,
        policyholderPassport: formData.passport_number || `TEMP${Date.now()}`,
        policyholderDateOfBirth: formData.date_of_birth,
        premium: quoteData.premium?.toString() || '0',
        coverageLimits: this.getCoverageLimits(prodCode),
        hidePremium: true,
      };

      // Add zone-specific fields
      if (quoteData.coverage_amount) {
        payload.medicalExpenseAmount = quoteData.coverage_amount.toString();
      }

      return payload;
    },

    getZoneAndProductCode(quoteData) {
      const destination = quoteData.destination?.toLowerCase() || '';
      const travelType = quoteData.travel_type?.toLowerCase() || '';
      const cover = quoteData.coverage_amount || 0;

      // EUROPE
      if (this.isEuropeDestination(destination)) {
        // Basic, Family Basic, Family Plus, Individual Plus
        if (travelType === 'family') {
          if (cover >= 150000) return { zone: 2, prodCode: 'EUR_FAM_PLU' };
          return { zone: 2, prodCode: 'EUR_FAM_BAS' };
        }
        if (cover >= 150000) return { zone: 2, prodCode: 'EUR_IND_PLU' };
        return { zone: 2, prodCode: 'EUR_BAS' };
      }

      // ASIA
      if (this.isAsiaDestination(destination)) {
        return {
          zone: 12,
          prodCode: travelType === 'family' ? 'ASI_FAM' : 'ASI_IND',
        };
      }

      // AMERICAS / WORLDWIDE
      if (this.isAmericasDestination(destination)) {
        // Basic, Extended, Premium versions
        if (travelType === 'family') {
          if (cover >= 200000) return { zone: 3, prodCode: 'WW_FAM_PRE' };
          if (cover >= 150000) return { zone: 3, prodCode: 'WW_FAM_EXT' };
          return { zone: 3, prodCode: 'WW_FAM_BAS' };
        }
        if (cover >= 200000) return { zone: 3, prodCode: 'WW_IND_PRE' };
        if (cover >= 150000) return { zone: 3, prodCode: 'WW_IND_EXT' };
        return { zone: 3, prodCode: 'WW_IND_BAS' };
      }

      // DEFAULT → AFRICA
      return {
        zone: 1,
        prodCode: travelType === 'family' ? 'AFR_FAM' : 'AFR_IND',
      };
    },

    isEuropeDestination(destination) {
      const europeCountries = [
        'france', 'germany', 'italy', 'spain', 'uk', 'united kingdom', 'netherlands',
        'switzerland', 'austria', 'belgium', 'portugal', 'greece', 'sweden',
        'norway', 'denmark', 'finland', 'poland', 'czech', 'hungary',
      ];
      return europeCountries.some((country) => destination.includes(country));
    },

    isAsiaDestination(destination) {
      const asiaCountries = [
        'china', 'japan', 'thailand', 'singapore', 'malaysia', 'indonesia',
        'philippines', 'vietnam', 'south korea', 'india', 'nepal', 'sri lanka',
      ];
      return asiaCountries.some((country) => destination.includes(country));
    },

    isAmericasDestination(destination) {
      const americasCountries = [
        'usa', 'united states', 'canada', 'mexico', 'brazil', 'argentina',
        'chile', 'colombia', 'peru', 'venezuela',
      ];
      return americasCountries.some((country) => destination.includes(country));
    },

    getCountryCode(destination) {
      if (!destination) return null;

      // Get comprehensive country list
      const countries = this.getDestinationCountries();
      const destinationLower = destination.toLowerCase();

      // Try to find exact match first
      const exactMatch = countries.find((country) => country.TRC_NAME.toLowerCase() === destinationLower);
      if (exactMatch) {
        return exactMatch.TRC_SHT_DESC;
      }

      // Try partial matching
      const partialMatch = countries.find((country) => country.TRC_NAME.toLowerCase().includes(destinationLower)
        || destinationLower.includes(country.TRC_NAME.toLowerCase()));
      if (partialMatch) {
        return partialMatch.TRC_SHT_DESC;
      }

      return null; // Return null if no match found
    },

    getDestinationCountries() {
      // Same comprehensive country list as StartTravel.vue
      const countries = [
        { TRC_SHT_DESC: 'DZ', TRC_NAME: 'ALGERIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'AO', TRC_NAME: 'ANGOLA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'BJ', TRC_NAME: 'BENIN', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'BW', TRC_NAME: 'BOTSWANA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'BF', TRC_NAME: 'BURKINA FASO', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'BI', TRC_NAME: 'BURUNDI', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'CM', TRC_NAME: 'CAMEROON', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'CV', TRC_NAME: 'CAPE VERDE', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'CF', TRC_NAME: 'CENTRAL AFRICAN REPUBLIC', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'TD', TRC_NAME: 'CHAD', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'KM', TRC_NAME: 'COMOROS', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'CG', TRC_NAME: 'CONGO', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'CD', TRC_NAME: 'CONGO (DEMOCRATIC REPUBLIC)', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'CI', TRC_NAME: 'COTE D\'IVOIRE', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'DJ', TRC_NAME: 'DJIBOUTI', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'EG', TRC_NAME: 'EGYPT', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'GQ', TRC_NAME: 'EQUATORIAL GUINEA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'ER', TRC_NAME: 'ERITREA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'ET', TRC_NAME: 'ETHIOPIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'GA', TRC_NAME: 'GABON', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'GM', TRC_NAME: 'GAMBIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'GH', TRC_NAME: 'GHANA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'GN', TRC_NAME: 'GUINEA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'GW', TRC_NAME: 'GUINEA-BISSAU', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'KE', TRC_NAME: 'KENYA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'LS', TRC_NAME: 'LESOTHO', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'LR', TRC_NAME: 'LIBERIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'LY', TRC_NAME: 'LIBYA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'MG', TRC_NAME: 'MADAGASCAR', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'MW', TRC_NAME: 'MALAWI', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'ML', TRC_NAME: 'MALI', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'MR', TRC_NAME: 'MAURITANIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'MU', TRC_NAME: 'MAURITIUS', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'YT', TRC_NAME: 'MAYOTTE', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'MA', TRC_NAME: 'MOROCCO', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'MZ', TRC_NAME: 'MOZAMBIQUE', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'NA', TRC_NAME: 'NAMIBIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'NE', TRC_NAME: 'NIGER', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'NG', TRC_NAME: 'NIGERIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'RE', TRC_NAME: 'REUNION', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'RW', TRC_NAME: 'RWANDA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'ST', TRC_NAME: 'SAO TOME AND PRINCIPE', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'SN', TRC_NAME: 'SENEGAL', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'SC', TRC_NAME: 'SEYCHELLES', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'SL', TRC_NAME: 'SIERRA LEONE', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'SO', TRC_NAME: 'SOMALIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'ZA', TRC_NAME: 'SOUTH AFRICA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'SS', TRC_NAME: 'SOUTH SUDAN', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'SD', TRC_NAME: 'SUDAN', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'SZ', TRC_NAME: 'ESWATINI (SWAZILAND)', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'TZ', TRC_NAME: 'TANZANIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'TG', TRC_NAME: 'TOGO', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'TN', TRC_NAME: 'TUNISIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'UG', TRC_NAME: 'UGANDA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'ZM', TRC_NAME: 'ZAMBIA', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'ZW', TRC_NAME: 'ZIMBABWE', TRC_TRZ_CODE: 1 },
        { TRC_SHT_DESC: 'AF', TRC_NAME: 'AFGHANISTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'AM', TRC_NAME: 'ARMENIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'AZ', TRC_NAME: 'AZERBAIJAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'BH', TRC_NAME: 'BAHRAIN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'BD', TRC_NAME: 'BANGLADESH', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'BT', TRC_NAME: 'BHUTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'BN', TRC_NAME: 'BRUNEI', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'KH', TRC_NAME: 'CAMBODIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'CN', TRC_NAME: 'CHINA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'GE', TRC_NAME: 'GEORGIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'HK', TRC_NAME: 'HONG KONG', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'IN', TRC_NAME: 'INDIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'ID', TRC_NAME: 'INDONESIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'IR', TRC_NAME: 'IRAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'IQ', TRC_NAME: 'IRAQ', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'IL', TRC_NAME: 'ISRAEL', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'JP', TRC_NAME: 'JAPAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'JO', TRC_NAME: 'JORDAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'KZ', TRC_NAME: 'KAZAKHSTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'KW', TRC_NAME: 'KUWAIT', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'KG', TRC_NAME: 'KYRGYZSTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'LA', TRC_NAME: 'LAOS', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'LB', TRC_NAME: 'LEBANON', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'MO', TRC_NAME: 'MACAU', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'MY', TRC_NAME: 'MALAYSIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'MV', TRC_NAME: 'MALDIVES', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'MN', TRC_NAME: 'MONGOLIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'MM', TRC_NAME: 'MYANMAR (BURMA)', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'NP', TRC_NAME: 'NEPAL', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'KP', TRC_NAME: 'NORTH KOREA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'OM', TRC_NAME: 'OMAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'PK', TRC_NAME: 'PAKISTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'PS', TRC_NAME: 'PALESTINE', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'PH', TRC_NAME: 'PHILIPPINES', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'QA', TRC_NAME: 'QATAR', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'SA', TRC_NAME: 'SAUDI ARABIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'SG', TRC_NAME: 'SINGAPORE', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'KR', TRC_NAME: 'SOUTH KOREA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'LK', TRC_NAME: 'SRI LANKA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'SY', TRC_NAME: 'SYRIA', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'TW', TRC_NAME: 'TAIWAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'TJ', TRC_NAME: 'TAJIKISTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'TH', TRC_NAME: 'THAILAND', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'TL', TRC_NAME: 'TIMOR-LESTE', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'TR', TRC_NAME: 'TURKEY', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'TM', TRC_NAME: 'TURKMENISTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'AE', TRC_NAME: 'UNITED ARAB EMIRATES', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'UZ', TRC_NAME: 'UZBEKISTAN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'VN', TRC_NAME: 'VIETNAM', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'YE', TRC_NAME: 'YEMEN', TRC_TRZ_CODE: 2 },
        { TRC_SHT_DESC: 'US', TRC_NAME: 'UNITED STATES', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'CA', TRC_NAME: 'CANADA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'MX', TRC_NAME: 'MEXICO', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'BZ', TRC_NAME: 'BELIZE', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'CR', TRC_NAME: 'COSTA RICA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'SV', TRC_NAME: 'EL SALVADOR', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'GT', TRC_NAME: 'GUATEMALA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'HN', TRC_NAME: 'HONDURAS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'NI', TRC_NAME: 'NICARAGUA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'PA', TRC_NAME: 'PANAMA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'AI', TRC_NAME: 'ANGUILLA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'AG', TRC_NAME: 'ANTIGUA AND BARBUDA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'AW', TRC_NAME: 'ARUBA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'BS', TRC_NAME: 'BAHAMAS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'BB', TRC_NAME: 'BARBADOS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'BM', TRC_NAME: 'BERMUDA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'VG', TRC_NAME: 'BRITISH VIRGIN ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'KY', TRC_NAME: 'CAYMAN ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'CU', TRC_NAME: 'CUBA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'CW', TRC_NAME: 'CURACAO', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'DM', TRC_NAME: 'DOMINICA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'DO', TRC_NAME: 'DOMINICAN REPUBLIC', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'GD', TRC_NAME: 'GRENADA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'GP', TRC_NAME: 'GUADELOUPE', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'HT', TRC_NAME: 'HAITI', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'JM', TRC_NAME: 'JAMAICA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'MQ', TRC_NAME: 'MARTINIQUE', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'MS', TRC_NAME: 'MONTSERRAT', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'PR', TRC_NAME: 'PUERTO RICO', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'KN', TRC_NAME: 'SAINT KITTS AND NEVIS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'LC', TRC_NAME: 'SAINT LUCIA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'VC', TRC_NAME: 'SAINT VINCENT AND THE GRENADINES', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'TT', TRC_NAME: 'TRINIDAD AND TOBAGO', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'TC', TRC_NAME: 'TURKS AND CAICOS ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'VI', TRC_NAME: 'US VIRGIN ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'AR', TRC_NAME: 'ARGENTINA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'BO', TRC_NAME: 'BOLIVIA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'BR', TRC_NAME: 'BRAZIL', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'CL', TRC_NAME: 'CHILE', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'CO', TRC_NAME: 'COLOMBIA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'EC', TRC_NAME: 'ECUADOR', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'FK', TRC_NAME: 'FALKLAND ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'GF', TRC_NAME: 'FRENCH GUIANA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'GY', TRC_NAME: 'GUYANA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'PY', TRC_NAME: 'PARAGUAY', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'PE', TRC_NAME: 'PERU', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'SR', TRC_NAME: 'SURINAME', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'UY', TRC_NAME: 'URUGUAY', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'VE', TRC_NAME: 'VENEZUELA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'AU', TRC_NAME: 'AUSTRALIA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'NZ', TRC_NAME: 'NEW ZEALAND', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'FJ', TRC_NAME: 'FIJI', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'PG', TRC_NAME: 'PAPUA NEW GUINEA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'NC', TRC_NAME: 'NEW CALEDONIA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'PF', TRC_NAME: 'FRENCH POLYNESIA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'SB', TRC_NAME: 'SOLOMON ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'VU', TRC_NAME: 'VANUATU', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'WS', TRC_NAME: 'SAMOA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'TO', TRC_NAME: 'TONGA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'CK', TRC_NAME: 'COOK ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'PW', TRC_NAME: 'PALAU', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'MH', TRC_NAME: 'MARSHALL ISLANDS', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'FM', TRC_NAME: 'MICRONESIA', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'KI', TRC_NAME: 'KIRIBATI', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'TV', TRC_NAME: 'TUVALU', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'NR', TRC_NAME: 'NAURU', TRC_TRZ_CODE: 3 },
        { TRC_SHT_DESC: 'AL', TRC_NAME: 'ALBANIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'AD', TRC_NAME: 'ANDORRA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'AT', TRC_NAME: 'AUSTRIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'BY', TRC_NAME: 'BELARUS', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'BE', TRC_NAME: 'BELGIUM', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'BA', TRC_NAME: 'BOSNIA AND HERZEGOVINA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'BG', TRC_NAME: 'BULGARIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'HR', TRC_NAME: 'CROATIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'CY', TRC_NAME: 'CYPRUS', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'CZ', TRC_NAME: 'CZECH REPUBLIC', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'DK', TRC_NAME: 'DENMARK', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'EE', TRC_NAME: 'ESTONIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'FI', TRC_NAME: 'FINLAND', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'FR', TRC_NAME: 'FRANCE', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'DE', TRC_NAME: 'GERMANY', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'GI', TRC_NAME: 'GIBRALTAR', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'GR', TRC_NAME: 'GREECE', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'HU', TRC_NAME: 'HUNGARY', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'IS', TRC_NAME: 'ICELAND', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'IE', TRC_NAME: 'IRELAND', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'IT', TRC_NAME: 'ITALY', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'XK', TRC_NAME: 'KOSOVO', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'LV', TRC_NAME: 'LATVIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'LI', TRC_NAME: 'LIECHTENSTEIN', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'LT', TRC_NAME: 'LITHUANIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'LU', TRC_NAME: 'LUXEMBOURG', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'MT', TRC_NAME: 'MALTA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'MD', TRC_NAME: 'MOLDOVA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'MC', TRC_NAME: 'MONACO', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'ME', TRC_NAME: 'MONTENEGRO', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'NL', TRC_NAME: 'NETHERLANDS', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'MK', TRC_NAME: 'NORTH MACEDONIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'NO', TRC_NAME: 'NORWAY', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'PL', TRC_NAME: 'POLAND', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'PT', TRC_NAME: 'PORTUGAL', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'RO', TRC_NAME: 'ROMANIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'RU', TRC_NAME: 'RUSSIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'SM', TRC_NAME: 'SAN MARINO', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'RS', TRC_NAME: 'SERBIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'SK', TRC_NAME: 'SLOVAKIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'SI', TRC_NAME: 'SLOVENIA', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'ES', TRC_NAME: 'SPAIN', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'SE', TRC_NAME: 'SWEDEN', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'CH', TRC_NAME: 'SWITZERLAND', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'UA', TRC_NAME: 'UKRAINE', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'GB', TRC_NAME: 'UNITED KINGDOM', TRC_TRZ_CODE: 4 },
        { TRC_SHT_DESC: 'VA', TRC_NAME: 'VATICAN CITY', TRC_TRZ_CODE: 4 },
      ];

      return countries.sort((a, b) => a.TRC_NAME.localeCompare(b.TRC_NAME));
    },

    getCoverageLimits(prodCode) {
      const baseCoverage = {
        medicalExpenses: '1',
        personalAssistance: '1',
        baggage: '1',
        travelDelay: '1',
      };

      if (prodCode.startsWith('EUR_')) {
        return {
          ...baseCoverage,
          winterSport: '1',
          hospitalization: '1',
          medicalTransportation: '1',
          accidentalDamagePhoneGadget: '1',
          curtailment: '1',
        };
      }

      if (prodCode === 'CORP_BUS') {
        return {
          ...baseCoverage,
          curtailment: '1',
          medicalTransportation: '1',
          personalLiability: '1',
          advanceOfBailBondLiability: '1',
          cancellation: '1',
        };
      }

      return baseCoverage;
    },
  },
};
</script>
